import User, { UserInviteStatus } from "model/User";
import { InvitationData } from "model/InvitationData";
import { Credentials } from "model/Credentials";
import {
  AccountRolePayload, fromAccountRolesToInvite, fromLocationRoles, LocationRolePayload, toAccountRole, toLocationRole
} from "./Role";
import { LocationResponse } from "./Location";
import { Account, Extendable } from "model";
import { UnitSystem } from "model/UnitSystem";
import { AlarmSettingsResponse } from "./AlarmSettings";

export interface UserInvitePayload {
  email: string;
  locale?: string;
  accountRoles: string[];
  accountId: string;
  locationRoles: LocationRolePayload[];
}

interface InvitationMetadata {
  tokenId: string;
  email: string;
  locale: string;
}

export interface InvitationDataResponse {
  token: string;
  metadata: InvitationMetadata;
}

export interface AcceptInvitationPayload {
  firstName: string;
  lastName: string;
  phoneMobile: string;
  password: string;
  locale?: string;
}

export interface UserPayload {
  id?: string;
  email: string;
  isActive?: boolean;
  firstName?: string;
  lastName?: string;
  unitSystem?: string;
  phoneMobile?: string;
  phone?: string;
  locale?: string;
  role?: string;
  permission?: string[];
  account?: Account;
  accountRole?: AccountRolePayload;
  locationRoles?: LocationRolePayload[];
  enabledFeatures?: string[];
}

export interface RegistrationData {
  locale?:            string;
  userAccountRole?:   AccountRolePayload;
  userLocationRoles?: LocationRolePayload[];
}

export interface PendingInviteResponse {
  email:                      string;
  createdAt?:                 string;
  tokenExpiresAt?:            string;
  registrationDataExpiresAt?: string;
  registrationData:           RegistrationData;
  accountId?:                 string;
}

export interface UserResponse extends UserPayload {
  locations?: Array<Extendable<LocationResponse>>;
  alarmSettings?: AlarmSettingsResponse;
}

const toUnitSystem = (unitSystem?: string): UnitSystem => {
  switch(unitSystem) {
    case 'metric_kpa':
      return UnitSystem.METRIC;
    case 'imperial_us':
      return UnitSystem.IMPERIAL;
    default:
      return UnitSystem.IMPERIAL;
  }
}

export const toUser = (data: UserPayload): User => ({
  id: data.id,
  email: data.email,
  isActive: data.isActive,
  firstName: data.firstName,
  lastName: data.lastName,
  locale: data.locale,
  unitSystem: toUnitSystem(data.unitSystem),
  phoneMobile: data.phoneMobile,
  account: data.account,
  accountRole: toAccountRole(data.accountRole),
  locationRoles: toLocationRole(data.locationRoles),
  status: UserInviteStatus.ACTIVE,
  enabledFeatures: data.enabledFeatures,
});

export const fromUser = (data: User): UserPayload => ({
  email: data.email,
  firstName: data.firstName,
  lastName: data.lastName,
  phoneMobile: data.phoneMobile,
});

export const fromUserToInvite = (data: User): UserInvitePayload => ({
  email: data.email,
  locale: data.locale,
  accountRoles: fromAccountRolesToInvite(data.accountRole),
  locationRoles: fromLocationRoles(data.locationRoles),
  accountId: data.account?.id || ''
});

export const toInvitationData = (apiResponse: InvitationDataResponse): InvitationData => ({
  token: apiResponse.token,
  metadata: {
    tokenId: apiResponse.metadata.tokenId,
    email: apiResponse.metadata.email,
    locale: apiResponse.metadata.locale
  }
})

export const fromUserToAcceptInvitation = (user: User, credentials: Credentials): AcceptInvitationPayload => {
  if (!user.firstName || !user.lastName || !user.phoneMobile || !credentials.password ) {
    throw new Error('Invalid parameters')
  }
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    phoneMobile: user.phoneMobile,
    password: credentials.password,
    locale: user.locale
  }
}

export const toUserFromPendingInvite = (data: PendingInviteResponse): User => ({
  id: data.createdAt,
  email: data.email,
  isActive: false,
  firstName: '',
  lastName: '',
  locale: data.registrationData?.locale,
  unitSystem: UnitSystem.IMPERIAL,
  phoneMobile: '',
  account: { id: data.accountId || '' },
  accountRole: toAccountRole(data.registrationData?.userAccountRole),
  locationRoles: toLocationRole(data.registrationData?.userLocationRoles),
  status: UserInviteStatus.PENDING,
});
